import { LazyQueryNetworkBoundrary } from "../../../../components/mobile/QueryNetworkBoundrary";
import { OrgButton } from "../../OrgButton";
import React, { useCallback, useEffect } from "react";
import { useAlert } from "../../../../common/Alert";

import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { WorkspaceSignalStackScreenProps } from "../WorkspaceSignalStack";
import { Box, TodoPrioIcon } from "arkh";
import { WorkspaceDetailHeader } from "../../WorkspaceDetailHeader";
import { WorkspaceSignalDetailScreenQuery } from "./__generated__/WorkspaceSignalDetailScreenQuery.graphql";
import { WorkspaceSignalStoresList } from "./WorkspaceSignalStoreList";
import { useIntl } from "react-intl";
import { useWorkspacePermissions, WorkspaceWritable } from "../../workspaceId";
import { WorkspaceDetailNavigationHeader } from "../../WorkspaceDetailNavigationHeader";
import { useWorkspaceSignalsHeaderBackTitle } from "../WorkspaceSignalArchived/useWorkspaceSignalsHeaderBackTitle";
import { HeaderButton } from "@/src/components/mobile/Button";
import { WorkspaceScheduledArchiveButton } from "../../WorkspaceTodoSignalShared/WorkspaceScheduledArchiveButton";
import { useWorkspaceSignalArchiveDescription } from "./useWorkspaceSignalArchiveDescription";
import { FormattedDateInWorkspace } from "@/src/common/dates/FormattedDate";

export function WorkspaceSignalDetailScreen(
  props: WorkspaceSignalStackScreenProps<"WorkspaceSignalDetail">
) {
  const headerBackTitle = useWorkspaceSignalsHeaderBackTitle();
  return (
    <>
      <LazyQueryNetworkBoundrary
        newFetchKeyOnNavigation
        fallback={
          <WorkspaceDetailNavigationHeader
            left={
              <HeaderButton
                type="WEB-BACK"
                customLabel={headerBackTitle}
                onPress={() => props.navigation.goBack()}
              />
            }
            right={null}
          />
        }
      >
        {(fk) => <WorkspaceSignalDetailScreenC {...props} fetchKey={fk} />}
      </LazyQueryNetworkBoundrary>
    </>
  );
}

function WorkspaceSignalDetailScreenC(
  props: WorkspaceSignalStackScreenProps<"WorkspaceSignalDetail"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<WorkspaceSignalDetailScreenQuery>(
    graphql`
      query WorkspaceSignalDetailScreenQuery(
        $workspaceId: ID!
        $signalId: ID!
      ) {
        workspace(id: $workspaceId) {
          id
          signal(id: $signalId) {
            id
            isWorkspaceOwner(workspaceId: $workspaceId)
            title
            archivedAtDate {
              ...FormattedDate
            }
            scheduledArchiveAt {
              ...FormattedDate
            }
            priority
            ...WorkspaceSignalStoreList_signalWorkspace
          }
        }
      }
    `,
    {
      workspaceId: props.route.params.workspaceId,
      signalId: props.route.params.signalWorkspaceId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [archiveSignal, archiveSignalLoading] = useMutation(graphql`
    mutation WorkspaceSignalDetailScreenArchiveMutation(
      $signalWorkspaceId: ID!
    ) {
      signalWorkspace(id: $signalWorkspaceId) {
        archive {
          id
          archivedAtDate {
            ...FormattedDate
          }
        }
      }
    }
  `);

  const [unarchiveSignal, unarchiveSignalLoading] = useMutation(graphql`
    mutation WorkspaceSignalDetailScreenUnarchiveMutation(
      $signalWorkspaceId: ID!
    ) {
      signalWorkspace(id: $signalWorkspaceId) {
        unarchive {
          id
          archivedAtDate {
            ...FormattedDate
          }
        }
      }
    }
  `);

  const intl = useIntl();
  const archiveTitle = intl.formatMessage({
    defaultMessage: "Archive Signal",
    description: "WorkspaceSignalDetail Archive Title",
  });

  const archiveDescription = useWorkspaceSignalArchiveDescription();

  const intl_tokens = useIntlTokens();
  const alert = useAlert();
  const onArchive = useCallback(() => {
    return alert.alert(archiveTitle, archiveDescription, [
      {
        text: intl_tokens.Cancel,
        style: "cancel",
      },
      {
        text: intl_tokens.Archive,
        isPreferred: true,
        style: "default",
        onPress: () => {
          archiveSignal({
            variables: {
              signalWorkspaceId: data.workspace.signal!.id,
            },
            onCompleted: () => {
              props.navigation.replace("WorkspaceSignalsArchived", {
                workspaceId: data.workspace.id,
              });
            },
          });
        },
      },
    ]);
  }, [
    alert,
    archiveTitle,
    archiveDescription,
    intl_tokens.Cancel,
    intl_tokens.Archive,
    archiveSignal,
    data.workspace.signal,
    data.workspace.id,
    props.navigation,
  ]);

  useEffect(() => {
    if (!data.workspace.signal) {
      props.navigation.pop();
    }
  }, [data.workspace.signal, props.navigation]);

  const headerBackTitle = useWorkspaceSignalsHeaderBackTitle();
  const onScheduleArchive = useCallback(() => {
    if (data.workspace.signal) {
      props.navigation.navigateDeprecated(
        "WorkspaceSignalEditScheduledArchiveAt",
        {
          workspaceId: data.workspace.id,
          workspaceSignalId: data.workspace.signal.id,
        }
      );
    }
  }, [data.workspace.signal, data.workspace.id, props.navigation]);

  const onScheduleArchiveEdit = useCallback(() => {
    if (data.workspace.signal) {
      props.navigation.navigateDeprecated(
        "WorkspaceSignalEditScheduledArchiveAt",
        {
          workspaceId: data.workspace.id,
          workspaceSignalId: data.workspace.signal.id,
        }
      );
    }
  }, [data.workspace.signal, data.workspace.id, props.navigation]);

  const { write } = useWorkspacePermissions();
  if (!data.workspace.signal) return null;

  return (
    <>
      <WorkspaceDetailNavigationHeader
        left={
          <HeaderButton
            type="WEB-BACK"
            customLabel={headerBackTitle}
            onPress={() => props.navigation.goBack()}
          />
        }
        right={
          <Box style={{ flexDirection: "row" }}>
            {data.workspace.signal.isWorkspaceOwner ? (
              <WorkspaceWritable>
                {data.workspace.signal.archivedAtDate ? (
                  <OrgButton
                    border="border-primary"
                    backgroundColor="surface-primary"
                    tintColor="text-secondary"
                    title={intl_tokens.Unarchive}
                    loading={unarchiveSignalLoading}
                    icon="archive"
                    onPress={() => {
                      unarchiveSignal({
                        variables: {
                          signalWorkspaceId: data.workspace.signal!.id,
                        },
                        onCompleted: () => {
                          props.navigation.navigateDeprecated(
                            "WorkspaceSignalsMain",
                            {}
                          );
                        },
                      });
                    }}
                  />
                ) : !data.workspace.signal.scheduledArchiveAt ? (
                  <Box spaceRight="small" style={{ flexDirection: "row" }}>
                    <Box spaceRight="small">
                      <OrgButton
                        backgroundColor="surface"
                        tintColor="text-secondary"
                        title={intl_tokens.ScheduleArchive}
                        icon="clock"
                        onPress={onScheduleArchive}
                        border="border-primary"
                      />
                    </Box>
                    <OrgButton
                      border="border-primary"
                      backgroundColor="surface"
                      tintColor="text-secondary"
                      title={intl_tokens.Archive}
                      icon="archive"
                      loading={archiveSignalLoading}
                      onPress={onArchive}
                    />
                  </Box>
                ) : null}
              </WorkspaceWritable>
            ) : null}
            {data.workspace.signal.scheduledArchiveAt &&
            !data.workspace.signal.archivedAtDate ? (
              <WorkspaceScheduledArchiveButton
                date={
                  <FormattedDateInWorkspace
                    date={data.workspace.signal.scheduledArchiveAt}
                  />
                }
                disabled={!(write && data.workspace.signal.isWorkspaceOwner)}
                onPress={onScheduleArchiveEdit}
              />
            ) : null}
          </Box>
        }
      />
      <Box
        style={{
          maxWidth: 900,
          width: "100%",
          alignSelf: "center",
        }}
        spaceHorizontal="medium"
        spaceTop="medium"
      >
        <WorkspaceDetailHeader
          color="surface-background-light"
          spaceBottom="none"
          spaceTop="none"
          title={data.workspace.signal.title}
          titleIcon={
            <TodoPrioIcon
              priority={data.workspace.signal.priority}
              hideDefault
            />
          }
        />
      </Box>
      <WorkspaceSignalStoresList
        openSignalOpenedBy={(
          signalId: string,
          storeName: string,
          storeTimezone: string
        ) => {
          props.navigation.navigateDeprecated("SignalOpenedByEmployee", {
            signalId,
            storeName,
            storeTimezone,
          });
        }}
        signalWorkspace={data.workspace.signal}
        openSignal={(
          signalId: string,
          storeName: string,
          storeTimezone: string
        ) => {
          props.navigation.navigateDeprecated("SignalDetail", {
            signalId,
            storeName,
            storeTimezone,
          });
        }}
      />
    </>
  );
}
